// Pure presentation helpers for the Forecast & Predictability dashboard. No React.
import { forecastLabUrl } from "../forecast-lab/deepLink";

export function humanEUR(v) {
  const n = Number(v) || 0;
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs < 1000) return `€${sign}${Math.round(abs)}`;
  const k = abs / 1000;
  if (k < 10) {
    const s = k.toFixed(1).replace(/\.0$/, "");
    return `€${sign}${s}k`;
  }
  return `€${sign}${Math.round(k)}k`;
}

export function statusTone(status) {
  switch (status) {
    case "green":
      return "ok";
    case "amber":
      return "attention";
    case "red":
      return "critical";
    default:
      return "neutral";
  }
}

const LEVEL_LABEL = { high: "High", medium: "Medium", low: "Low" };

// toBandSeries adds a `band: [lower, upper]` tuple to points that have both bounds,
// for a recharts range Area. Points without both bounds are passed through unchanged.
export function toBandSeries(series) {
  return (series || []).map((p) =>
    p.lower != null && p.upper != null ? { ...p, band: [p.lower, p.upper] } : p
  );
}

// formatForecastChange renders the week-over-week forecast movement, or null when
// there's no prior-week snapshot yet.
export function formatForecastChange(change) {
  if (!change || !change.available) return null;
  const abs = Number(change.delta_abs) || 0;
  const isNeg = abs < 0;
  const eur = humanEUR(Math.abs(abs));
  const signed = isNeg ? `-${eur}` : `+${eur}`;
  const pctNum = change.delta_pct == null ? null : Math.round(Math.abs(change.delta_pct) * 100);
  const dir = isNeg ? "down" : "up";
  return pctNum == null
    ? `${signed} since last week`
    : `${signed} (${dir} ${pctNum}%) since last week`;
}

// bridgeBars turns a ChangeBridge into ordered waterfall bars:
// [prior total, ...signed deltas, current total]. Returns [] when there's nothing to show.
export function bridgeBars(bridge) {
  if (!bridge || !Array.isArray(bridge.deltas) || bridge.deltas.length === 0) return [];
  const bars = [{ kind: "total", label: "Prior 7 days", value: Number(bridge.prior_actual) || 0 }];
  for (const d of bridge.deltas) {
    bars.push({ kind: "delta", key: d.key, label: d.label, amount: Number(d.amount) || 0, sources: Array.isArray(d.sources) ? d.sources : [] });
  }
  bars.push({ kind: "total", label: "Last 7 days", value: Number(bridge.current_actual) || 0 });
  return bars;
}

// buildForecastTiles maps the kpis payload to the 4-tile strip view-model. `change` (the
// week-over-week forecast movement) is folded into the Projected tile so it isn't an orphaned line.
export function buildForecastTiles(kpis = {}, horizon = 90, change = null) {
  const conf = kpis.forecast_confidence || {};
  const budget = kpis.budget_risk || {};

  // projected — point value, with the confidence RANGE + week-over-week drift in the sub-line, so
  // the figure carries its uncertainty instead of reading as false precision.
  const lo = kpis.projected_lower;
  const hi = kpis.projected_upper;
  const hasRange = lo != null && hi != null && hi > lo && lo > 0;
  const rangeStr = hasRange ? `${humanEUR(lo)}–${humanEUR(hi)}` : null;
  let wow = null;
  if (change && change.available) {
    const a = Number(change.delta_abs) || 0;
    wow = `${a >= 0 ? "+" : "-"}${humanEUR(Math.abs(a))} wk`;
  }
  const projWhy = [rangeStr ? `range ${rangeStr}` : "forecast spend", wow].filter(Boolean).join(" · ");

  // confidence
  let confValue = "Not yet rated";
  let confWhy = "accuracy pending";
  if (conf.level && conf.level !== "not_yet_rated") {
    confValue = LEVEL_LABEL[conf.level] || conf.level;
    confWhy = conf.mape != null ? `±${Math.round(conf.mape * 100)}% recent error` : "";
  }

  // budget headroom (worst budget) — reworded so an over-budget reads "9% over", not "headroom −9%".
  // Drills into the Forecast Lab's budgets lens (this dashboard's analytical companion) rather than
  // the operational budgets page, preselecting the worst budget so the forecast behind the number is
  // one click away.
  let budgetValue = "Not yet available";
  let budgetWhy = "";
  let budgetHref = forecastLabUrl({ tab: "explorer", scope: "insights", lens: "budgets", horizon });
  const wb = budget.worst_budget;
  if (budget.status && budget.status !== "neutral") {
    if (wb && wb.projected_pct != null) {
      const overPct = Math.round((wb.projected_pct - 1) * 100);
      budgetValue = overPct > 0 ? `${overPct}% over` : `${Math.abs(overPct)}% left`;
    }
    if (wb) {
      budgetHref = forecastLabUrl({ tab: "explorer", scope: "insights", lens: "budgets", horizon, selected: wb.id });
      if (wb.days_to_breach != null) budgetWhy = `${wb.name} breach in ${wb.days_to_breach}d`;
      else if (wb.projected_pct != null) budgetWhy = `${wb.name} at ${Math.round(wb.projected_pct * 100)}% of cap`;
      else budgetWhy = wb.name;
    }
  } else if (budget.note === "no_active_budgets") {
    budgetValue = "No active budgets";
  }

  // budgets at risk (replaces AI share) — the portfolio "N of M over" read, with the total budget
  // in a tooltip. Far more central to "what will we spend" than AI composition.
  const counts = budget.counts || {};
  const over = Number(counts.over) || 0;
  const atRisk = Number(counts.at_risk) || 0;
  const totalBudgets = over + atRisk + (Number(counts.on_track) || 0) + (Number(counts.not_measurable) || 0);
  let riskValue = "—";
  let riskWhy = "no active budgets";
  if (totalBudgets > 0) {
    riskValue = `${over + atRisk} of ${totalBudgets}`;
    riskWhy = over > 0 ? `${over} over budget` : atRisk > 0 ? `${atRisk} at risk` : "all on track";
  }
  const totalCap = Number(kpis.total_budget_cap) || 0;
  const riskInfo = totalCap > 0 ? `${humanEUR(totalCap)} total budget across ${totalBudgets} budget${totalBudgets === 1 ? "" : "s"}` : undefined;

  return [
    { key: "projected", label: `Projected (${horizon}d)`, value: humanEUR(kpis.projected), why: projWhy, tone: "neutral", href: "/cost-analyzer" },
    { key: "confidence", label: "Confidence", value: confValue, why: confWhy, tone: statusTone(conf.status), href: "#" },
    { key: "budget", label: "Budget headroom", value: budgetValue, why: budgetWhy, tone: statusTone(budget.status), href: budgetHref },
    { key: "budgets", label: "Budgets at risk", value: riskValue, why: riskWhy, tone: statusTone(budget.status), href: forecastLabUrl({ tab: "explorer", scope: "insights", lens: "budgets", horizon }), info: riskInfo },
  ];
}
